import React, { useState, useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";

import CloseIcon from "@mui/icons-material/CloseOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";

import AppContext from "../appContext";
import importQuestions from "../Editor/importQuestions";
import References from "../UserTimeline/TimelineItem/References";
import CustomTreeView from "./CustomTreeView";
import UseSurveyButton from "./UseSurveyButton";
import { useDeleteSearchParam, useGetSeachParam } from "../useSearchParams";
import getDescription from "../UserTimeline/TimelineItem/getDescription";
import usePublicInteraction from "./usePublicInteraction";
import useAccountSettings from "../ManageAccount/useAccountSettings";
import useActiveContexts from "../Library/useActiveContexts";
import { useTranslation } from "react-i18next";
import { exportQuestionsFromLib } from "../Editor/exportQuestions";
import { CircularProgress } from "@mui/material";
import { useLocation } from "react-router-dom";
import { logging } from "../tools";

//   // library item
// const item = {
//   id: -1,
//   userId: -1,
//   name: "Survey",
//   description: "",
//   public: -1,
//   lastChanged: "2021-01-20 21:48:39",
//   firstName: "Maarten",
//   lastName: "Piot",
//   code: "test",
//   homepage: null,
//   tagIds: [],
//   N: -1,
// };

const initState = {
  questions: [],
  details: {},
  description: getDescription(),
  interaction: null,
};

export default function Interaction() {
  const textHeaderStyle = {
    paddingTop: 3,
    wordBreak: "break-word",
  };
  const { t } = useTranslation();
  const interactionId = Number(useGetSeachParam("i"));
  const closeInteraction = useDeleteSearchParam("i");
  const {
    app: {
      client: { id: clientId, writeAccess },
    },
  } = useContext(AppContext);
  const [questions, setQuestions] = useState(initState.questions);
  const [details, setDetails] = useState(initState.details);
  const [description, setDescription] = useState(initState.description);
  const [interaction, setInteraction] = useState(initState.interaction);
  const isCurate = details?.itemExtra?.interactionView === 1;

  // server state
  const {
    accountSettings: { code: myPractitionerCode },
  } = useAccountSettings(); // load the interaction
  const { publicInteraction, isPublicInteractionLoading } =
    usePublicInteraction(interactionId);
  const isEditable = publicInteraction?.from?.code === myPractitionerCode;
  const { contexts } = useActiveContexts();
  const location = useLocation();
  const showNotVerifiedDisclaimer = location.pathname === "/library/public"; // show not verified alert in the public library. TODO: Define exceptions
  const getSharedWidth = () => {
    switch (publicInteraction?.from?.public) {
      case 0:
        return t("library.shareDialog.none");
      case 1:
        return t("library.shareDialog.public");
      default:
        return contexts.find(
          (context) => context.contextId === publicInteraction?.from?.public
        )?.name;
    }
  };
  const sharedWidth =
    isEditable && publicInteraction?.from?.public !== 0
      ? getSharedWidth()
      : null;

  useEffect(() => {
    if (publicInteraction?.interaction) {
      setInteraction(publicInteraction.interaction);
      const readQuestion = importQuestions(publicInteraction.interaction);
      setQuestions(readQuestion.questions); // survey with questions
      setDetails(readQuestion.details); // details of survey
      setDescription(readQuestion.description);
    } else {
      setQuestions(initState.questions);
      setDetails(initState.details);
      setDescription(initState.description);
      setInteraction(initState.interaction);
    }
  }, [publicInteraction]);

  if (isPublicInteractionLoading)
    return <CircularProgress sx={{ margin: 2 }} />;
  if (publicInteraction)
    return (
      <Box
        sx={{
          flexGrow: 1,
          borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
          padding: 2,
          height: "100%",
        }}
      >
        <Box
          sx={{
            backgroundColor: "background.default",
            color: "text.secondary",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              width: "100%",
              paddingBottom: 2,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="h4">{publicInteraction?.name}</Typography>
                {sharedWidth ? (
                  <Tooltip title={t("library.shared")}>
                    <PeopleAltOutlinedIcon />
                  </Tooltip>
                ) : null}
              </Box>
              <Box display="flex" gap={1} alignItems={"center"}>
                <Typography variant="subtitle2">
                  {t("library.from")}: {publicInteraction?.from?.firstName}{" "}
                  {publicInteraction?.from?.lastName} @
                  {publicInteraction?.from?.code} &#x2022;{" "}
                  {description.licenseInfo !== ""
                    ? description.licenseInfo
                    : t("interactions.freeLicense")}
                </Typography>
              </Box>
              {sharedWidth ? (
                <Typography variant="subtitle2">
                  {t("library.sharedWidth")}: {sharedWidth}
                </Typography>
              ) : null}
            </div>
            <div>
              <IconButton
                component={Link}
                to={{ search: closeInteraction }}
                size="small"
                sx={{ padding: 1 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </Box>
          {showNotVerifiedDisclaimer ? (
            <Box paddingBottom={2}>
              <Alert severity="info">
                {t("interactions.notVerifiedDisclaimer")}
              </Alert>
            </Box>
          ) : null}
          <Box>
            {interaction ? (
              <UseSurveyButton
                from="library"
                interactionId={interactionId}
                questionList={{
                  ...interaction,
                  fullQuestion: publicInteraction.name,
                }}
                onButtonClick={() => {
                  if (isCurate) {
                    logging("Export curated questionnaire");
                    const exportedQuestionList = exportQuestionsFromLib(
                      questions,
                      details
                    );
                    logging(exportedQuestionList);
                    setInteraction(exportedQuestionList);
                  }
                }}
              />
            ) : null}
          </Box>
        </Box>
        <div>
          {/*  <div className={overlay}>
           */}
          {description.description !== "" ? (
            <div>
              <Typography
                variant="h6"
                color="textPrimary"
                component="p"
                sx={textHeaderStyle}
              >
                {t("interactions.description")}
              </Typography>
              <Typography
                sx={{ whiteSpace: "pre-line", wordWrap: "break-word" }}
              >
                {description.description}
              </Typography>
            </div>
          ) : null}
          {description.descriptionItems !== "" ? (
            <div>
              <Typography
                variant="h6"
                color="textPrimary"
                component="p"
                sx={textHeaderStyle}
              >
                {t("interactions.descriptionItems")}
              </Typography>
              <Typography>{description.descriptionItems}</Typography>
            </div>
          ) : null}
          {questions.length > 0 ? (
            <div>
              <Typography
                variant="h6"
                color="textPrimary"
                component="p"
                sx={textHeaderStyle}
              >
                {t("interactions.items")}
              </Typography>
              <CustomTreeView
                questions={questions}
                isCurate={isCurate}
                locked={clientId <= 0}
                handleUpdateTree={(tree) => setQuestions(tree)}
              />
            </div>
          ) : null}
          {description.tags.length !== 0 ? (
            <div>
              <Typography
                variant="h6"
                color="textPrimary"
                component="p"
                sx={textHeaderStyle}
              >
                {t("interactions.tags")}
              </Typography>
              <Box flexGrow={1}>
                {description.tags.map(({ id, label }) => (
                  <Chip key={id} sx={{ marginRight: 1 }} label={label} />
                ))}
              </Box>
            </div>
          ) : null}
          {description.references.length !== 0 ? (
            <div>
              <Typography
                variant="h6"
                color="textPrimary"
                component="p"
                sx={textHeaderStyle}
              >
                {t("interactions.references")}
              </Typography>
              <Grid
                container
                spacing={2}
                direction="row"
                justifyContent="flex-start"
                alignItems="stretch"
              >
                {description.references.map((reference) => (
                  <Grid key={"MoreInfo" + reference.id} item xs>
                    <References reference={reference} />
                  </Grid>
                ))}
              </Grid>
            </div>
          ) : null}
        </div>
        <Box padding={2} />
      </Box>
    );
  return <div></div>;
}
